import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Types for Org Data API (backend-synced entities)
 */
export interface OrgDivision {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface OrgPlatform {
  id: string;
  name: string;
  division_id: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface OrgChapter {
  id: string;
  name: string;
  platform_id: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface OrgDataApiResponse {
  divisions: Array<{
    id: string;
    name: string;
    platforms: Array<{
      id: string;
      name: string;
      chapters?: Array<{ id: string; name: string }>;
    }>;
  }>;
}

export interface CreateDivisionRequest {
  id: string;
  name: string;
}

export interface UpdateDivisionRequest {
  name: string;
}

export interface CreatePlatformRequest {
  id: string;
  name: string;
  divisionId: string;
}

export interface UpdatePlatformRequest {
  name?: string;
  divisionId?: string;
}

export interface CreateChapterRequest {
  id: string;
  name: string;
  platformId: string;
}

export interface UpdateChapterRequest {
  name?: string;
  platformId?: string;
}

/**
 * Org Data API interface — used to read/write dynamic org data from the backend.
 */
export interface OrgDataApi {
  // Read operations
  getOrgData(): Promise<OrgDataApiResponse>;
  getDivisions(): Promise<{ divisions: OrgDivision[] }>;
  getDivision(id: string): Promise<OrgDivision>;
  getPlatforms(divisionId?: string): Promise<{ platforms: OrgPlatform[] }>;
  getPlatform(id: string): Promise<OrgPlatform>;
  getChapters(platformId?: string): Promise<{ chapters: OrgChapter[] }>;
  getChapter(id: string): Promise<OrgChapter>;

  // Write operations (admin only)
  createDivision(data: CreateDivisionRequest): Promise<OrgDivision>;
  updateDivision(id: string, data: UpdateDivisionRequest): Promise<OrgDivision>;
  deleteDivision(id: string): Promise<void>;

  createPlatform(data: CreatePlatformRequest): Promise<OrgPlatform>;
  updatePlatform(id: string, data: UpdatePlatformRequest): Promise<OrgPlatform>;
  deletePlatform(id: string): Promise<void>;

  createChapter(data: CreateChapterRequest): Promise<OrgChapter>;
  updateChapter(id: string, data: UpdateChapterRequest): Promise<OrgChapter>;
  deleteChapter(id: string): Promise<void>;
}

/**
 * API reference for the Org Data backend API.
 * Register `OrgDataClient` (from `packages/app`) against this ref in your app.
 */
export const orgDataApiRef = createApiRef<OrgDataApi>({
  id: 'plugin.org-data.api',
});

/**
 * Minimal fetch-based implementation of OrgDataApi.
 * The full-featured `OrgDataClient` lives in `packages/app/src/apis/OrgDataClient.ts`
 * and re-exports this ref for backwards compatibility.
 */
export class OrgDataClientBase implements OrgDataApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('org-data');
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };
    const response = await this.fetchApi.fetch(url, { ...options, headers });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Request failed with status ${response.status}: ${body}`,
      );
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  async getOrgData(): Promise<OrgDataApiResponse> {
    return this.fetch<OrgDataApiResponse>('/org-data');
  }

  async getDivisions(): Promise<{ divisions: OrgDivision[] }> {
    return this.fetch<{ divisions: OrgDivision[] }>('/divisions');
  }

  async getDivision(id: string): Promise<OrgDivision> {
    return this.fetch<OrgDivision>(`/divisions/${encodeURIComponent(id)}`);
  }

  async getPlatforms(divisionId?: string): Promise<{ platforms: OrgPlatform[] }> {
    const query = divisionId
      ? `?divisionId=${encodeURIComponent(divisionId)}`
      : '';
    return this.fetch<{ platforms: OrgPlatform[] }>(`/platforms${query}`);
  }

  async getPlatform(id: string): Promise<OrgPlatform> {
    return this.fetch<OrgPlatform>(`/platforms/${encodeURIComponent(id)}`);
  }

  async getChapters(platformId?: string): Promise<{ chapters: OrgChapter[] }> {
    const query = platformId
      ? `?platformId=${encodeURIComponent(platformId)}`
      : '';
    return this.fetch<{ chapters: OrgChapter[] }>(`/chapters${query}`);
  }

  async getChapter(id: string): Promise<OrgChapter> {
    return this.fetch<OrgChapter>(`/chapters/${encodeURIComponent(id)}`);
  }

  async createDivision(data: CreateDivisionRequest): Promise<OrgDivision> {
    return this.fetch<OrgDivision>('/divisions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDivision(id: string, data: UpdateDivisionRequest): Promise<OrgDivision> {
    return this.fetch<OrgDivision>(`/divisions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDivision(id: string): Promise<void> {
    return this.fetch<void>(`/divisions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async createPlatform(data: CreatePlatformRequest): Promise<OrgPlatform> {
    return this.fetch<OrgPlatform>('/platforms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlatform(id: string, data: UpdatePlatformRequest): Promise<OrgPlatform> {
    return this.fetch<OrgPlatform>(`/platforms/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePlatform(id: string): Promise<void> {
    return this.fetch<void>(`/platforms/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async createChapter(data: CreateChapterRequest): Promise<OrgChapter> {
    return this.fetch<OrgChapter>('/chapters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChapter(id: string, data: UpdateChapterRequest): Promise<OrgChapter> {
    return this.fetch<OrgChapter>(`/chapters/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteChapter(id: string): Promise<void> {
    return this.fetch<void>(`/chapters/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}
